// services/pdfCoverService.js
//
// Turning a PDF invitation card into page images.
//
// ── Why this is a server's job ─────────────────────────────────────────────
//
// A PDF has to be rasterised before it can be a cover, and doing that in a
// browser means shipping pdf.js — several megabytes, on a bundle that is
// already 3.4MB — and then re-uploading every rendered page from the client.
// The product already has the service that does it: a Cloud Run container
// with Ghostscript behind it (invits-app-new/functions/pdf-service.js), which
// the app has used since PDF covers shipped.
//
// It takes a Supabase bearer token, which the website has, and it sets CORS,
// so the browser can call it directly. The pages land on the same CDN path
// the app's do, so a PDF cover made here is indistinguishable from one made
// in the app — including to the invitation page, which already renders the
// page gallery.
//
// ── Why it needs the invitation to exist first ─────────────────────────────
//
// The service files pages under `events/{eventId}/pdf_pages`, so there has to
// be an event id. That is why the cover is chosen before saving and converted
// after — the same order the app uses.

import supabase from './supabase';

const PDF_SERVICE_URL =
  process.env.REACT_APP_PDF_SERVICE_URL ||
  'https://pdf-converter-676512905826.asia-south1.run.app';

/**
 * Converts a PDF to page images and uploads them.
 *
 * @param {{base64: string, fileName: string}} pdf
 * @param {string} eventId
 * @returns {Promise<{pdfPages: Array<{page:number, cdn_url:string, direct_url:string, path:string}>, totalPages:number, fileName:string}>}
 */
export async function convertAndUploadPdf(pdf, eventId) {
  if (!pdf?.base64) throw new Error('No PDF to convert.');
  if (!eventId) throw new Error('The invitation must be saved before its PDF can be converted.');

  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('Not signed in.');

  // Ghostscript on a fifty-page PDF is not quick, and the service allows
  // itself nine minutes. An AbortController is the only way to bound a fetch;
  // without one a hung request leaves the publish button spinning for ever.
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 9 * 60 * 1000);

  let response;
  try {
    response = await fetch(`${PDF_SERVICE_URL}/convertAndUploadPdf`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        eventId,
        pdfFile: pdf.base64,
        fileName: pdf.fileName,
      }),
      signal: controller.signal,
    });
  } catch (err) {
    if (err.name === 'AbortError') {
      throw new Error('Converting the PDF took too long. Try a smaller file.');
    }
    throw new Error('Could not reach the PDF converter.');
  } finally {
    clearTimeout(timeout);
  }

  if (!response.ok) {
    let message = `The PDF converter said no (${response.status}).`;
    try {
      const body = await response.json();
      if (body?.error) message = body.error;
    } catch {
      // Keep the status-code message.
    }
    throw new Error(message);
  }

  const body = await response.json();
  if (!body?.success) throw new Error(body?.error || 'Failed to convert the PDF.');

  // The service returns cdnUrl/b2Url/fileName/pageNumber; the column stores
  // cdn_url/direct_url/path/page. The app transforms it the same way, and the
  // invitation page reads the snake_case shape — so this mapping is not a
  // preference, it is the contract.
  const pdfPages = (body.data?.pageImages ?? []).map((page) => ({
    page: page.pageNumber,
    cdn_url: page.cdnUrl,
    direct_url: page.b2Url,
    path: page.fileName,
  }));

  if (pdfPages.length === 0) throw new Error('That PDF produced no pages.');

  return {
    pdfPages,
    totalPages: body.data?.totalPages ?? pdfPages.length,
    fileName: pdf.fileName,
  };
}

/**
 * The columns a converted PDF writes on the event row.
 *
 * The first page doubles as the ordinary cover, so everything that shows a
 * cover without knowing about PDFs — the home list, link previews, the app's
 * own cards — gets a picture rather than nothing.
 */
export function pdfCoverColumns({ pdfPages }) {
  const first = pdfPages[0];
  return {
    cover_image_type: 'pdf',
    pdf_page_images: pdfPages,
    cover_image_cdn_url: first.cdn_url,
    cover_image_direct_url: first.direct_url,
    cover_image_path: first.path,
  };
}
